import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCustomer, getProspect, getCustomerVisits, getCustomerCalls, getCustomerSales, createVisit, createCall, createSale, uploadCustomerFile, uploadSaleFile, getFileDownloadUrl, getUsers, updateCustomer, updateProspect, getProducts, deleteCustomerFile, analyzeCustomer, getOffers, getSubscriptionOffers } from '@/api/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { toast } from 'sonner';
import { ArrowLeft, MapPin, Phone as PhoneIcon, Mail, User, Package, Upload, FileText, Calendar, Download, ChevronDown, Edit, Eye, PhoneCall, ShoppingCart, Trash2, Brain, Plus, X } from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

const CustomerDetail = ({ user: userProp, isProspect = false }) => {
  const { customerId, prospectId } = useParams();
  const actualId = isProspect ? prospectId : customerId;
  const navigate = useNavigate();
  const [user, setUser] = useState(userProp || JSON.parse(localStorage.getItem('user') || 'null'));
  const [customer, setCustomer] = useState(null);
  const [visits, setVisits] = useState([]);
  const [calls, setCalls] = useState([]);
  const [sales, setSales] = useState([]);
  const [offers, setOffers] = useState([]);
  const [subscriptionOffers, setSubscriptionOffers] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showVisitDialog, setShowVisitDialog] = useState(false);
  const [showCallDialog, setShowCallDialog] = useState(false);
  const [showSaleDialog, setShowSaleDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCompleteInfoDialog, setShowCompleteInfoDialog] = useState(false);
  const [showCategoryDetailsDialog, setShowCategoryDetailsDialog] = useState(false);
  const [selectedCategoryDetails, setSelectedCategoryDetails] = useState({ category: '', sales: [] });
  const [visitNotes, setVisitNotes] = useState('');
  const [visitDate, setVisitDate] = useState('today');
  const [callData, setCallData] = useState({ call_type: 'Genel', notes: '' });
  const [callDate, setCallDate] = useState('today');
  // YENİ: Çoklu ürün satış state'leri
  const [saleItems, setSaleItems] = useState([]);
  const [currentSaleItem, setCurrentSaleItem] = useState({
    product_id: '',
    product_name: '',
    category: 'Peşin Satış',
    quantity: 1,
    unit_price: 0
  });
  const [saleNotes, setSaleNotes] = useState('');
  const [saleDate, setSaleDate] = useState('today');
  
  // ESKİ: Tek ürün satış (geriye dönük uyumluluk - kullanılmıyor artık)
  const [saleData, setSaleData] = useState({ 
    product_id: '', 
    product_name: '', 
    quantity: 1, 
    price: '', 
    notes: '' 
  });
  const [uploadingFile, setUploadingFile] = useState(false);
  const [openVisitId, setOpenVisitId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [showAiAnalysisDialog, setShowAiAnalysisDialog] = useState(false);
  const [analyzingCustomer, setAnalyzingCustomer] = useState(false);

  // Tarih hesaplama yardımcı fonksiyonu
  const getDateFromSelection = (dateSelection) => {
    const now = new Date();
    if (dateSelection === 'yesterday') {
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      return yesterday.toISOString();
    }
    return now.toISOString(); // 'today' veya default
  };

  useEffect(() => {
    // Load user info from localStorage
    const userInfo = localStorage.getItem('user');
    if (userInfo) {
      try {
        setUser(JSON.parse(userInfo));
      } catch (e) {
        console.error('User parsing error:', e);
      }
    }
    
    loadData();
    loadUsers();
    loadProducts();
  }, [actualId]);

  const loadUsers = async () => {
    try {
      const usersData = await getUsers();
      setUsers(usersData);
    } catch (error) {
      console.error('Kullanıcılar yüklenemedi');
    }
  };

  const loadProducts = async () => {
    try {
      const productsData = await getProducts();
      setProducts(productsData);
    } catch (error) {
      console.error('Ürünler yüklenemedi');
    }
  };

  const loadData = async () => {
    try {
      const getDataFunc = isProspect ? getProspect : getCustomer;
      
      // Her API çağrısını ayrı ayrı handle et - birinin başarısız olması diğerlerini etkilemesin
      const safeApiCall = async (apiCall, defaultValue = []) => {
        try {
          return await apiCall();
        } catch (error) {
          // 403 hatası rol tabanlı erişim kısıtlaması - sessizce atla
          if (error.response?.status === 403) {
            console.log('Access restricted for this endpoint');
            return defaultValue;
          }
          throw error; // Diğer hataları yukarı fırlat
        }
      };
      
      // Müşteri verisi kritik - hata olursa catch bloğuna düş
      const customerData = await getDataFunc(actualId);
      
      // Diğer verileri paralel olarak al, her biri bağımsız hata yönetimine sahip
      const [visitsData, callsData, salesData, offersData, subscriptionOffersData] = await Promise.all([
        safeApiCall(() => getCustomerVisits(actualId)),
        safeApiCall(() => getCustomerCalls(actualId)),
        safeApiCall(() => getCustomerSales(actualId)),
        safeApiCall(() => getOffers(actualId)),
        safeApiCall(() => getSubscriptionOffers(actualId))
      ]);
      
      setCustomer(customerData);
      setVisits(visitsData);
      setCalls(callsData);
      setSales(salesData);
      setOffers(offersData);
      setSubscriptionOffers(subscriptionOffersData);
      setEditFormData(customerData);
    } catch (error) {
      console.error('Error loading customer data:', error);
      toast.error('Veriler yüklenirken hata oluştu: ' + (error.response?.data?.detail || error.message));
      // If token error, redirect to login
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVisitSubmit = async () => {
    try {
      await createVisit({
        customer_id: actualId,
        notes: visitNotes,
        visit_date: getDateFromSelection(visitDate)
      });
      toast.success('Ziyaret kaydedildi');
      setShowVisitDialog(false);
      setVisitNotes('');
      setVisitDate('today');
      loadData();
    } catch (error) {
      toast.error('Ziyaret kaydedilirken hata oluştu');
    }
  };

  const handleCallSubmit = async () => {
    try {
      await createCall({
        customer_id: actualId,
        call_type: callData.call_type,
        notes: callData.notes,
        call_date: getDateFromSelection(callDate)
      });
      toast.success('Arama kaydedildi');
      setShowCallDialog(false);
      setCallData({ call_type: 'Genel', notes: '' });
      setCallDate('today');
      loadData();
    } catch (error) {
      toast.error('Arama kaydedilirken hata oluştu');
    }
  };

  const handleSaleSubmit = async () => {
    try {
      // Müşteri adayı için eksik bilgileri kontrol et
      if (isProspect) {
        const missingFields = [];
        if (!customer.phone) missingFields.push('Telefon');
        if (!customer.address) missingFields.push('Adres');
        if (!customer.city) missingFields.push('Şehir');
        if (!customer.tax_number) missingFields.push('Vergi No');
        if (!customer.tax_office) missingFields.push('Vergi Dairesi');
        
        if (missingFields.length > 0) {
          setShowCompleteInfoDialog(true);
          setShowSaleDialog(false);
          return;
        }
      }
      
      // Temsilci ve Abonelik rolleri için portföy kontrolü
      if (user?.role === 'temsilci' || user?.role === 'abonelik') {
        if (customer.representative !== user.username) {
          toast.error('Portföyünüzde olmayan bir müşteriye satış yapamazsınız');
          return;
        }
      }
      
      // YENİ: Çoklu ürün kontrolü
      if (saleItems.length === 0) {
        toast.error('Lütfen en az bir ürün ekleyin');
        return;
      }
      
      const totalPrice = calculateSaleGrandTotal();
      
      const sale = await createSale({
        customer_id: actualId,
        items: saleItems.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          category: item.category,
          quantity: item.quantity,
          unit_price: item.unit_price,
          discount_percentage: 0
        })),
        notes: saleNotes,
        sale_date: getDateFromSelection(saleDate)
      });
      
      // Satış yapıldığında otomatik olarak ziyaret kaydı oluştur
      const itemsSummary = saleItems.map(item => `${item.product_name} (${item.quantity} adet)`).join(', ');
      await createVisit({
        customer_id: actualId,
        notes: `Satış yapıldı - ${itemsSummary} - ${totalPrice.toLocaleString('tr-TR')} ₺`,
        sale_made: true,
        visit_date: getDateFromSelection(saleDate)
      });
      
      toast.success('Satış kaydedildi');
      setShowSaleDialog(false);
      setSaleItems([]);
      setCurrentSaleItem({ product_id: '', product_name: '', category: 'Peşin Satış', quantity: 1, unit_price: 0 });
      setSaleNotes('');
      setSaleDate('today');
      setSelectedProduct(null);
      
      // Eğer prospect'ten satış yapıldıysa, artık customer olmuştur
      // Müşteriler listesine yönlendir
      if (isProspect) {
        toast.success('Müşteri adayı başarıyla müşteriye dönüştürüldü!');
        setTimeout(() => {
          navigate('/customers');
        }, 1500);
      } else {
        loadData();
      }
    } catch (error) {
      toast.error('Satış kaydedilirken hata oluştu');
    }
  };

  // YENİ: Çoklu ürün için product select
  const handleProductSelect = (productId) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      setSelectedProduct(product);
      setCurrentSaleItem({
        ...currentSaleItem,
        product_id: product.id,
        product_name: product.name,
        category: product.category || 'Peşin Satış',
        unit_price: product.sale_price || 0
      });
    }
  };

  // YENİ: Ürün ekle
  const handleAddSaleItem = () => {
    if (!currentSaleItem.product_name || currentSaleItem.quantity <= 0 || currentSaleItem.unit_price <= 0) {
      toast.error('Lütfen tüm ürün bilgilerini doldurun');
      return;
    }

    const newItem = {
      ...currentSaleItem,
      id: Date.now().toString()
    };

    setSaleItems([...saleItems, newItem]);
    setCurrentSaleItem({
      product_id: '',
      product_name: '',
      category: 'Peşin Satış',
      quantity: 1,
      unit_price: 0
    });
    setSelectedProduct(null);
    toast.success('Ürün eklendi');
  };

  // YENİ: Ürün sil
  const handleRemoveSaleItem = (itemId) => {
    setSaleItems(saleItems.filter(item => item.id !== itemId));
    toast.success('Ürün kaldırıldı');
  };

  // YENİ: Hesaplamalar (indirim yok)
  const calculateSaleItemTotal = (item) => {
    return item.unit_price * item.quantity;
  };

  const calculateSaleGrandTotal = () => {
    return saleItems.reduce((sum, item) => sum + calculateSaleItemTotal(item), 0);
  };

  // Kategori detaylarını göster
  const handleCategoryClick = (categoryName) => {
    // Filtreleme: seçilen kategoriye göre satışları filtrele
    let filteredSales = [];
    
    switch(categoryName) {
      case 'Toplam Cihaz':
        filteredSales = sales.filter(s => 
          ['Peşin Satış', 'Arvento Taksitli', 'Operatör Satışı', 'Proje Satışı'].includes(s.category)
        );
        break;
      case 'Kamera':
        filteredSales = sales.filter(s => s.category === 'Kamera Ürünleri');
        break;
      case 'Sensör':
        filteredSales = sales.filter(s => s.category === 'Sensörler');
        break;
      case 'M2M':
        filteredSales = sales.filter(s => s.category === 'M2M');
        break;
    }
    
    setSelectedCategoryDetails({
      category: categoryName,
      sales: filteredSales
    });
    setShowCategoryDetailsDialog(true);
  };

  // Toplam satış fiyatı hesaplama (birim fiyat × miktar)
  const calculateTotalPrice = () => {
    const quantity = parseInt(saleData.quantity) || 1;
    const unitPrice = parseFloat(saleData.price) || 0;
    return unitPrice * quantity;
  };

  // Puan hesaplama: Toplam Satış - (Alış × Miktar)
  const calculateProfit = () => {
    if (!selectedProduct || !saleData.price) return 0;
    const quantity = parseInt(saleData.quantity) || 1;
    const unitPrice = parseFloat(saleData.price) || 0;
    const purchasePrice = selectedProduct.purchase_price || 0;
    
    const totalSalePrice = unitPrice * quantity;
    const totalPurchaseCost = purchasePrice * quantity;
    
    return totalSalePrice - totalPurchaseCost;
  };

  // Toplam fiyat hesaplama (KDV hesaplama yok)
  const calculateTotalWithVAT = () => {
    const totalPrice = calculateTotalPrice();
    return totalPrice; // Sadece toplam birim fiyatı döndür
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Dosya boyutu 10MB\'dan büyük olamaz');
      e.target.value = null; // Reset input
      return;
    }

    setUploadingFile(true);
    try {
      await uploadCustomerFile(actualId, file);
      toast.success('Dosya yüklendi');
      e.target.value = null; // Reset input
      loadData();
    } catch (error) {
      console.error('File upload error:', error);
      toast.error('Dosya yüklenirken hata oluştu');
      e.target.value = null; // Reset input
    } finally {
      setUploadingFile(false);
    }
  };

  const handleAiAnalysis = async () => {
    setAnalyzingCustomer(true);
    try {
      const result = await analyzeCustomer(actualId);
      setAiAnalysis(result);
      setShowAiAnalysisDialog(true);
      toast.success('AI analizi tamamlandı');
    } catch (error) {
      console.error('AI analysis error:', error);
      toast.error('AI analizi sırasında hata oluştu');
    } finally {
      setAnalyzingCustomer(false);
    }
  };

  const handleFileDelete = async (fileId) => {
    if (!window.confirm('Bu dosyayı silmek istediğinize emin misiniz?')) {
      return;
    }
    
    try {
      await deleteCustomerFile(actualId, fileId);
      toast.success('Dosya silindi');
      loadData();
    } catch (error) {
      console.error('File delete error:', error);
      toast.error('Dosya silinirken hata oluştu');
    }
  };

  const handleUpdateCustomer = async () => {
    try {
      // Prospect mı customer mı kontrol et
      const updateFunc = isProspect ? updateProspect : updateCustomer;
      await updateFunc(actualId, {
        name: editFormData.name,
        phone: editFormData.phone,
        email: editFormData.email,
        address: editFormData.address,
        city: editFormData.city,
        district: editFormData.district,
        representative: editFormData.representative,
        device_count: editFormData.device_count,
        inactive_device_count: editFormData.inactive_device_count,
        arvento_count: editFormData.arvento_count,
        camera_count: editFormData.camera_count,
        tax_number: editFormData.tax_number,
        tax_office: editFormData.tax_office,
        contact_person: editFormData.contact_person
      });
      toast.success(isProspect ? 'Müşteri adayı bilgileri güncellendi' : 'Müşteri bilgileri güncellendi');
      setShowEditDialog(false);
      loadData();
    } catch (error) {
      console.error('Update error:', error);
      // Handle validation errors from FastAPI (detail can be array of objects)
      let errorMessage = 'Bilgiler güncellenirken hata oluştu';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          // FastAPI validation error format - extract field and message
          const fieldErrors = detail.map(err => `${err.loc?.[1] || 'Alan'}: ${err.msg}`).join(', ');
          errorMessage += ': ' + fieldErrors;
        } else if (typeof detail === 'string') {
          errorMessage += ': ' + detail;
        } else {
          errorMessage += ': ' + JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMessage += ': ' + error.message;
      }
      toast.error(errorMessage);
    }
  };

  if (loading) {
    return <div className="text-center py-12">Yükleniyor...</div>;
  }

  if (!customer) {
    return <div className="text-center py-12">Müşteri bulunamadı</div>;
  }

  return (
    <div className="space-y-6" data-testid="customer-detail-page">
      <div>
        <Button
          variant="outline"
          onClick={() => navigate('/customers')}
          className="mb-4"
          data-testid="back-to-customers-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Geri Dön
        </Button>
        <h1 className="text-4xl font-bold text-gray-800">{ customer.name}</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Customer Info */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold progo-text">Müşteri Bilgileri</h2>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowEditDialog(true)}
                data-testid="edit-customer-button"
              >
                <Edit className="w-4 h-4 mr-2" />
                Düzenle
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center space-x-3">
                <User className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Yetkili Adı</p>
                  <p className="font-medium">{customer.contact_person || '-'}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <PhoneIcon className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Telefon</p>
                  <p className="font-medium">{customer.phone || '-'}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">E-posta</p>
                  <p className="font-medium">{customer.email || '-'}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <MapPin className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">İlçe</p>
                  <p className="font-medium">{customer.district || '-'}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <MapPin className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Şehir</p>
                  <p className="font-medium">{customer.city || '-'}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <FileText className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Vergi Numarası</p>
                  <p className="font-medium">{customer.tax_number || '-'}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <FileText className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Vergi Dairesi</p>
                  <p className="font-medium">{customer.tax_office || '-'}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <User className="w-5 h-5 text-gray-400" />
                <div className="flex-1">
                  <p className="text-sm text-gray-500">Temsilci</p>
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-lg progo-text">{customer.representative || 'Atanmamış'}</p>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowEditDialog(true)}
                      className="text-blue-600 hover:text-blue-800 text-xs"
                    >
                      Değiştir
                    </Button>
                  </div>
                </div>
              </div>
              <div className="col-span-2 flex items-center space-x-3">
                <MapPin className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Adres</p>
                  <p className="font-medium">{customer.address || '-'}</p>
                </div>
              </div>
              <div 
                className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
                onClick={() => handleCategoryClick('Toplam Cihaz')}
              >
                <Package className="w-5 h-5 text-green-400" />
                <div>
                  <p className="text-sm text-gray-500">Toplam Cihaz Adedi</p>
                  <p className="text-2xl font-bold text-green-600">{customer.device_count || 0}</p>
                </div>
              </div>
              <div 
                className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
                onClick={() => handleCategoryClick('Pasif Cihaz')}
              >
                <Package className="w-5 h-5 text-red-400" />
                <div>
                  <p className="text-sm text-gray-500">Pasif Cihaz Adedi</p>
                  <p className="text-2xl font-bold text-red-600">{customer.inactive_device_count || 0}</p>
                </div>
              </div>
              <div 
                className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
                onClick={() => handleCategoryClick('Kamera')}
              >
                <Package className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Kamera Adedi</p>
                  <p className="text-2xl font-bold text-gray-800">{customer.camera_count || 0}</p>
                </div>
              </div>
              <div 
                className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
                onClick={() => handleCategoryClick('Sensör')}
              >
                <Package className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Sensör Adedi</p>
                  <p className="text-2xl font-bold text-gray-800">{customer.sensors_count || 0}</p>
                </div>
              </div>
              <div 
                className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
                onClick={() => handleCategoryClick('M2M')}
              >
                <Package className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">M2M Adedi</p>
                  <p className="text-2xl font-bold text-gray-800">{customer.m2m_count || 0}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Visit History */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-semibold progo-text mb-4">Ziyaret Geçmişi</h2>
            <div className="space-y-3">
              {visits.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Henüz ziyaret kaydı yok</p>
              ) : (
                visits.map((visit, index) => (
                  <Collapsible key={visit.id} open={openVisitId === visit.id} onOpenChange={() => setOpenVisitId(openVisitId === visit.id ? null : visit.id)}>
                    <div className="border rounded-lg hover:bg-gray-50 transition-colors" data-testid={`visit-item-${index}`}>
                      <CollapsibleTrigger className="w-full p-4 text-left">
                        <div className="flex justify-between items-start">
                          <div className="flex items-center space-x-2 flex-1">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span className="font-medium">
                              {format(new Date(visit.visit_date), 'dd MMMM yyyy HH:mm', { locale: tr })}
                            </span>
                            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${openVisitId === visit.id ? 'rotate-180' : ''}`} />
                          </div>
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="px-4 pb-4">
                        <div className="pt-2 border-t mt-2">
                          <p className="text-sm text-gray-600 mb-2">
                            <strong>Temsilci:</strong> {visit.representative}
                          </p>
                          {visit.notes && (
                            <p className="text-sm text-gray-700 mb-2">
                              <strong>Notlar:</strong> {visit.notes}
                            </p>
                          )}
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                ))
              )}
            </div>
          </div>

          {/* Call History */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-semibold progo-text mb-4">Aramalar Geçmişi</h2>
            <div className="space-y-3">
              {calls.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Henüz arama kaydı yok</p>
              ) : (
                calls.map((call, index) => (
                  <div key={call.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors" data-testid={`call-item-${index}`}>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <span className="font-medium">
                            {format(new Date(call.call_date), 'dd MMMM yyyy HH:mm', { locale: tr })}
                          </span>
                        </div>
                        <div className="space-y-1 text-sm">
                          <p className="text-gray-600">
                            <strong>Temsilci:</strong> {call.representative}
                          </p>
                          <p className="text-gray-600">
                            <strong>Arama Tipi:</strong> {call.call_type}
                          </p>
                          {call.notes && (
                            <p className="text-gray-700 mt-2">
                              <strong>Notlar:</strong> {call.notes}
                            </p>
                          )}
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        call.call_type === 'Abonelik' ? 'bg-blue-100 text-blue-800' :
                        call.call_type === 'Portföy' ? 'bg-purple-100 text-purple-800' :
                        call.call_type === 'Hoşgeldin' ? 'bg-green-100 text-green-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {call.call_type}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-semibold progo-text mb-4">İşlemler</h2>
            <div className="space-y-3">
              <Dialog open={showVisitDialog} onOpenChange={setShowVisitDialog}>
                <DialogTrigger asChild>
                  <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="visit-customer-button">
                    Ziyaret Et
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Yeni Ziyaret</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Ziyaret Tarihi</Label>
                      <Select value={visitDate} onValueChange={setVisitDate}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="today">Bugün</SelectItem>
                          <SelectItem value="yesterday">Dün</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Not</Label>
                      <Textarea
                        value={visitNotes}
                        onChange={(e) => setVisitNotes(e.target.value)}
                        placeholder="Ziyaret notları..."
                        rows={4}
                        data-testid="visit-notes-input"
                      />
                    </div>
                    <div className="flex flex-col sm:flex-row justify-end gap-2 sm:space-x-3">
                      <Button variant="outline" onClick={() => setShowVisitDialog(false)} className="w-full sm:w-auto">İptal</Button>
                      <Button onClick={handleVisitSubmit} className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto" data-testid="save-visit-button">
                        Kaydet
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Arama Yap Dialog */}
              <Dialog open={showCallDialog} onOpenChange={setShowCallDialog}>
                <DialogTrigger asChild>
                  <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white" data-testid="call-customer-button">
                    <PhoneCall className="w-4 h-4 mr-2" />
                    Arama Yap
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Yeni Arama</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Arama Tarihi</Label>
                      <Select value={callDate} onValueChange={setCallDate}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="today">Bugün</SelectItem>
                          <SelectItem value="yesterday">Dün</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Arama Tipi</Label>
                      <Select value={callData.call_type} onValueChange={(value) => setCallData({...callData, call_type: value})}>
                        <SelectTrigger data-testid="call-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Genel">Genel</SelectItem>
                          <SelectItem value="Abonelik">Abonelik</SelectItem>
                          <SelectItem value="Portföy">Portföy</SelectItem>
                          <SelectItem value="Hoşgeldin">Hoşgeldin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Not</Label>
                      <Textarea
                        value={callData.notes}
                        onChange={(e) => setCallData({...callData, notes: e.target.value})}
                        placeholder="Arama notları..."
                        rows={4}
                        data-testid="call-notes-input"
                      />
                    </div>
                    <Button onClick={handleCallSubmit} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-call-button">
                      Kaydet
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Satış Yap Dialog */}
              <Dialog open={showSaleDialog} onOpenChange={setShowSaleDialog}>
                <DialogTrigger asChild>
                  <Button className="w-full bg-green-600 hover:bg-green-700 text-white" data-testid="sale-customer-button">
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    Satış Yap
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[95vh] flex flex-col">
                  <DialogHeader className="flex-shrink-0">
                    <DialogTitle>Satış Bilgileri</DialogTitle>
                  </DialogHeader>
                  <div className="flex-1 overflow-y-auto pr-2 space-y-4">
                    {/* Satış Tarihi */}
                    <div>
                      <Label>Satış Tarihi</Label>
                      <Select value={saleDate} onValueChange={setSaleDate}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="today">Bugün</SelectItem>
                          <SelectItem value="yesterday">Dün</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Ürün Ekleme Formu */}
                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <h3 className="text-sm font-bold text-gray-800 mb-3">Ürün Ekle</h3>
                      
                      <div className="space-y-3">
                        {/* Ürün Seçimi */}
                        <div>
                          <Label>Ürün *</Label>
                          <Select 
                            value={currentSaleItem.product_id} 
                            onValueChange={handleProductSelect}
                          >
                            <SelectTrigger data-testid="product-select">
                              <SelectValue placeholder="Ürün seçin..." />
                            </SelectTrigger>
                            <SelectContent className="max-h-[400px]">
                              {/* Kategorilere göre grupla */}
                              {Object.entries(
                                products
                                  .filter(p => p.for_sales)
                                  .reduce((acc, product) => {
                                    const category = product.category || 'Diğer';
                                    if (!acc[category]) acc[category] = [];
                                    acc[category].push(product);
                                    return acc;
                                  }, {})
                              )
                              .sort((a, b) => {
                                // Özel sıralama
                                const categoryOrder = [
                                  'Peşin Satış',
                                  'Arvento Taksitli',
                                  'Operatör Satışı',
                                  'Proje Satışı',
                                  'M2M',
                                  'Sensörler',
                                  'Kamera Ürünleri'
                                ];
                                const indexA = categoryOrder.indexOf(a[0]);
                                const indexB = categoryOrder.indexOf(b[0]);
                                
                                // Eğer ikisi de listede varsa sıralarına göre
                                if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                                // A listede var, B yok - A önce
                                if (indexA !== -1) return -1;
                                // B listede var, A yok - B önce
                                if (indexB !== -1) return 1;
                                // İkisi de listede yok - alfabetik
                                return a[0].localeCompare(b[0], 'tr');
                              })
                              .map(([category, categoryProducts]) => (
                                <div key={category}>
                                  <div className="px-2 py-2 text-xs font-semibold text-gray-500 bg-gray-100 sticky top-0">
                                    {category} ({categoryProducts.length})
                                  </div>
                                  {categoryProducts
                                    .sort((a, b) => a.name.localeCompare(b.name, 'tr'))
                                    .map(product => (
                                      <SelectItem key={product.id} value={product.id} className="pl-6">
                                        <div className="flex justify-between items-center w-full">
                                          <span className="font-medium">{product.name}</span>
                                          <span className="text-green-600 font-semibold ml-2">{product.sale_price} ₺</span>
                                        </div>
                                      </SelectItem>
                                    ))}
                                </div>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Seçili Ürün Bilgisi */}
                        {selectedProduct && (
                          <div className="p-2 bg-blue-50 rounded text-sm">
                            <span className="font-medium">{selectedProduct.category}</span> - 
                            Alış: {selectedProduct.purchase_price} ₺
                          </div>
                        )}

                        {/* Miktar ve Fiyat */}
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label>Adet *</Label>
                            <Input
                              type="number"
                              min="1"
                              value={currentSaleItem.quantity}
                              onChange={(e) => setCurrentSaleItem({...currentSaleItem, quantity: parseInt(e.target.value) || 1})}
                            />
                          </div>
                          <div>
                            <Label>Birim Fiyat (₺) *</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={currentSaleItem.unit_price}
                              onChange={(e) => setCurrentSaleItem({...currentSaleItem, unit_price: parseFloat(e.target.value) || 0})}
                            />
                          </div>
                        </div>

                        <Button
                          onClick={handleAddSaleItem}
                          className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                          type="button"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Ürün Ekle
                        </Button>
                      </div>
                    </div>

                    {/* Eklenen Ürünler Listesi */}
                    {saleItems.length > 0 && (
                      <div className="border border-gray-200 rounded-lg p-4">
                        <h3 className="text-sm font-bold text-gray-800 mb-3">Eklenen Ürünler ({saleItems.length})</h3>
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {saleItems.map((item, index) => {
                            const itemTotal = calculateSaleItemTotal(item);
                            
                            return (
                              <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold text-gray-900">{index + 1}.</span>
                                    <span className="font-medium text-gray-800">{item.product_name}</span>
                                    <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">{item.category}</span>
                                  </div>
                                  <div className="text-sm text-gray-600 mt-1">
                                    {item.quantity} adet × {item.unit_price.toFixed(2)} ₺
                                    <span className="font-semibold ml-2">= {itemTotal.toFixed(2)} ₺</span>
                                  </div>
                                </div>
                                <Button
                                  onClick={() => handleRemoveSaleItem(item.id)}
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            );
                          })}
                        </div>

                        {/* Genel Toplam */}
                        <div className="mt-4 pt-4 border-t border-gray-300">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-lg font-bold text-gray-900">GENEL TOPLAM:</span>
                            <span className="text-2xl font-bold text-green-600">{calculateSaleGrandTotal().toFixed(2)} ₺</span>
                          </div>
                          <div className="flex justify-between items-center pt-2 border-t">
                            <span className="text-base font-semibold text-gray-700">Toplam Puan:</span>
                            <span className="text-xl font-bold text-blue-600">
                              {saleItems.reduce((sum, item) => {
                                const product = products.find(p => p.id === item.product_id);
                                if (!product || !product.purchase_price) return sum;
                                const discountedPrice = item.unit_price * (1 - (item.discount_percentage || 0) / 100);
                                const itemTotal = discountedPrice * item.quantity;
                                const itemCost = (product.purchase_price || 0) * item.quantity;
                                return sum + (itemTotal - itemCost);
                              }, 0).toFixed(2)} ₺
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                  </div>
                  <div className="flex-shrink-0 pt-4 border-t mt-4">
                    <Button onClick={handleSaleSubmit} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-sale-button">
                      Satışı Kaydet
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Teklif Oluştur Button */}
              <Button 
                className="w-full bg-orange-600 hover:bg-orange-700 text-white" 
                data-testid="create-offer-button"
                onClick={() => navigate('/offers', { state: { customerId: customer.id, customerName: customer.name } })}
              >
                <FileText className="w-4 h-4 mr-2" />
                Teklif Oluştur
              </Button>

              {/* AI Analiz Button */}
              <Button 
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white" 
                onClick={handleAiAnalysis}
                disabled={analyzingCustomer}
              >
                <Brain className="w-4 h-4 mr-2" />
                {analyzingCustomer ? 'Analiz Ediliyor...' : 'AI ile Analiz Yap'}
              </Button>

              <div>
                <Label className="text-sm text-gray-600 mb-2 block">Dosya Ekle (Max 10MB)</Label>
                <Button
                  variant="outline"
                  className="w-full"
                  disabled={uploadingFile}
                  onClick={() => document.getElementById('file-upload').click()}
                  data-testid="upload-file-button"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploadingFile ? 'Yükleniyor...' : 'Dosya Yükle'}
                </Button>
                <input
                  id="file-upload"
                  type="file"
                  onChange={handleFileUpload}
                  className="hidden"
                  data-testid="file-upload-input"
                />
              </div>
            </div>
          </div>

          {/* Files */}
          {customer.files && customer.files.length > 0 && (
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-semibold progo-text mb-4">Dosyalar</h2>
              <div className="space-y-2">
                {customer.files.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                    <div className="flex items-center space-x-2 flex-1">
                      <FileText className="w-4 h-4 text-gray-400" />
                      <div className="flex-1">
                        <p className="text-sm font-medium">{file.file_name}</p>
                        <span className="text-xs text-gray-500">
                          {format(new Date(file.uploaded_at), 'dd MMM yyyy HH:mm', { locale: tr })}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setPreviewFile(file)}
                        className="text-blue-600 hover:text-blue-800"
                        data-testid={`preview-file-${index}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <a
                        href={getFileDownloadUrl(file.file_id)}
                        download
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-600 hover:text-green-800"
                      >
                        <Download className="w-4 h-4" />
                      </a>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleFileDelete(file.file_id)}
                        className="text-red-600 hover:text-red-800"
                        data-testid={`delete-file-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Offers */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-semibold progo-text mb-4">Teklifler</h2>
            <div className="space-y-3">
              {offers.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Henüz teklif oluşturulmamış</p>
              ) : (
                offers.map((offer, index) => (
                  <div key={offer.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-semibold text-lg">{offer.offer_number}</div>
                        <p className="text-sm text-gray-600">{offer.title}</p>
                      </div>
                      <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                        {offer.total_amount.toFixed(2)} TL
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-3 h-3" />
                          <span>{format(new Date(offer.created_at), 'dd MMM yyyy', { locale: tr })}</span>
                        </div>
                        <div>
                          Geçerlilik: {format(new Date(offer.valid_until), 'dd MMM yyyy', { locale: tr })}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate('/offers')}
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        Görüntüle
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Abonelik Teklifleri */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-semibold progo-text mb-4">Abonelik Teklifleri</h2>
            <div className="space-y-3">
              {subscriptionOffers.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Henüz abonelik teklifi oluşturulmamış</p>
              ) : (
                subscriptionOffers.map((offer, index) => (
                  <div key={offer.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors border-purple-200">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-semibold text-lg text-purple-700">{offer.offer_number}</div>
                        <p className="text-sm text-gray-600">{offer.title}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {offer.items?.length || 1} ürün • {offer.duration_months} Ay
                        </p>
                      </div>
                      <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium">
                        {offer.total_amount?.toFixed(2)} TL
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-3 h-3" />
                        <span>{format(new Date(offer.created_at), 'dd MMM yyyy', { locale: tr })}</span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate('/subscription-offers')}
                        className="border-purple-300 text-purple-700 hover:bg-purple-50"
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        Görüntüle
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Sales History */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-semibold progo-text mb-4">Satış Geçmişi</h2>
            <div className="space-y-3">
              {sales.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Henüz satış kaydı yok</p>
              ) : (
                sales.map((sale, index) => (
                  <div key={sale.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors" data-testid={`sale-item-${index}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="font-medium">
                          {format(new Date(sale.sale_date), 'dd MMMM yyyy HH:mm', { locale: tr })}
                        </span>
                      </div>
                      {sale.items && sale.items.length > 1 ? (
                        <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium">
                          {sale.items.length} Ürün
                        </span>
                      ) : (
                        <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                          {sale.quantity} Adet
                        </span>
                      )}
                    </div>
                    
                    {/* Çoklu ürün gösterimi */}
                    {sale.items && sale.items.length > 1 ? (
                      <div className="mb-2">
                        <p className="text-sm font-semibold text-gray-700 mb-2">Ürünler:</p>
                        <div className="space-y-1 pl-4 border-l-2 border-purple-200">
                          {sale.items.map((item, itemIndex) => (
                            <div key={itemIndex} className="text-sm text-gray-700">
                              <span className="font-medium">{item.product_name}</span>
                              <span className="text-gray-500"> - {item.quantity} adet × {item.unit_price.toFixed(2)} ₺</span>
                              {item.discount_percentage > 0 && (
                                <span className="text-green-600 ml-1">(-{item.discount_percentage}%)</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-700 mb-1">
                        <strong>Ürün:</strong> {sale.product_name || 'Ürün Bilgisi Yok'}
                      </p>
                    )}
                    <p className="text-sm text-gray-600 mb-1">
                      <strong>Temsilci:</strong> {sale.representative}
                    </p>
                    <div className="flex justify-between items-center mb-1">
                      <p className="text-sm text-gray-700">
                        <strong>Satış Tutarı:</strong> <span className="text-green-600 font-semibold">{(sale.price || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺</span>
                      </p>
                      {sale.profit !== undefined && (
                        <p className="text-sm text-gray-700">
                          <strong>Puan:</strong> <span className={`font-semibold ${sale.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {(sale.profit || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                          </span>
                        </p>
                      )}
                    </div>
                    {sale.notes && (
                      <p className="text-sm text-gray-700 mb-2">
                        <strong>Notlar:</strong> {sale.notes}
                      </p>
                    )}
                    {sale.files && sale.files.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm font-medium text-gray-700 mb-1">Dosyalar:</p>
                        {sale.files.map((file, fileIndex) => (
                          <button
                            key={fileIndex}
                            onClick={async () => {
                              const url = await getFileDownloadUrl(file.file_id);
                              window.open(url, '_blank');
                            }}
                            className="text-sm text-blue-600 hover:underline flex items-center space-x-1 mr-3"
                          >
                            <FileText className="w-3 h-3" />
                            <span>{file.file_name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* File Preview Dialog */}
      <Dialog open={!!previewFile} onOpenChange={() => setPreviewFile(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{previewFile?.file_name}</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {previewFile && (
              <div className="flex items-center justify-center bg-gray-50 rounded-lg p-4">
                {previewFile.file_name.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                  <img
                    src={getFileDownloadUrl(previewFile.file_id)}
                    alt={previewFile.file_name}
                    className="max-w-full h-auto rounded-lg"
                  />
                ) : previewFile.file_name.match(/\.(pdf)$/i) ? (
                  <iframe
                    src={getFileDownloadUrl(previewFile.file_id)}
                    className="w-full h-[70vh] rounded-lg"
                    title={previewFile.file_name}
                  />
                ) : (
                  <div className="text-center py-12">
                    <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-600 mb-4">Bu dosya türü önizlenemiyor</p>
                    <a
                      href={getFileDownloadUrl(previewFile.file_id)}
                      download
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      İndir
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Category Details Dialog */}
      <Dialog open={showCategoryDetailsDialog} onOpenChange={setShowCategoryDetailsDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedCategoryDetails.category} Satışları</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedCategoryDetails.sales.length === 0 ? (
              <p className="text-center text-gray-500 py-8">Bu kategoride satış bulunamadı</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Ürün Adı</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Adet</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Satış Fiyatı</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Tarih</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {selectedCategoryDetails.sales.map((sale, index) => (
                      <tr key={sale.id || index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">{sale.product_name}</td>
                        <td className="px-4 py-3 text-sm text-center text-gray-900">{sale.quantity}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900">
                          {sale.price.toLocaleString('tr-TR')} ₺
                        </td>
                        <td className="px-4 py-3 text-sm text-center text-gray-600">
                          {new Date(sale.sale_date).toLocaleDateString('tr-TR')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900">Toplam</td>
                      <td className="px-4 py-3 text-sm text-center font-semibold text-gray-900">
                        {selectedCategoryDetails.sales.reduce((sum, s) => sum + s.quantity, 0)} Adet
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">
                        {selectedCategoryDetails.sales.reduce((sum, s) => sum + s.price, 0).toLocaleString('tr-TR')} ₺
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Customer Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Müşteri Bilgilerini Düzenle</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Müşteri Adı *</Label>
                <Input
                  value={editFormData.name || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  data-testid="edit-customer-name"
                />
              </div>
              <div>
                <Label>Yetkili Adı</Label>
                <Input
                  value={editFormData.contact_person || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, contact_person: e.target.value })}
                  data-testid="edit-customer-contact-person"
                />
              </div>
              <div>
                <Label>Telefon</Label>
                <Input
                  value={editFormData.phone || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                  data-testid="edit-customer-phone"
                />
              </div>
              <div>
                <Label>E-posta</Label>
                <Input
                  value={editFormData.email || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                  data-testid="edit-customer-email"
                />
              </div>
              <div>
                <Label>Vergi Numarası</Label>
                <Input
                  value={editFormData.tax_number || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, tax_number: e.target.value })}
                  data-testid="edit-customer-tax-number"
                />
              </div>
              <div>
                <Label>Vergi Dairesi</Label>
                <Input
                  value={editFormData.tax_office || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, tax_office: e.target.value })}
                  data-testid="edit-customer-tax-office"
                />
              </div>
              <div>
                <Label>İlçe</Label>
                <Input
                  value={editFormData.district || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, district: e.target.value })}
                  data-testid="edit-customer-district"
                />
              </div>
              <div>
                <Label>Şehir</Label>
                <Input
                  value={editFormData.city || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, city: e.target.value })}
                  data-testid="edit-customer-city"
                />
              </div>
              <div className="col-span-2">
                <Label>Adres</Label>
                <Input
                  value={editFormData.address || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
                  data-testid="edit-customer-address"
                />
              </div>
              <div>
                <Label>Temsilci</Label>
                <Select 
                  value={editFormData.representative || ''} 
                  onValueChange={(value) => setEditFormData({ ...editFormData, representative: value })}
                >
                  <SelectTrigger data-testid="edit-customer-representative">
                    <SelectValue placeholder="Temsilci seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(user => (
                      <SelectItem key={user.id} value={user.username}>{user.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Toplam Cihaz Adedi</Label>
                <Input
                  type="number"
                  value={editFormData.device_count || 0}
                  onChange={(e) => setEditFormData({ ...editFormData, device_count: parseInt(e.target.value) || 0 })}
                  data-testid="edit-customer-device-count"
                />
              </div>
              <div>
                <Label>Pasif Cihaz Adedi</Label>
                <Input
                  type="number"
                  value={editFormData.inactive_device_count || 0}
                  onChange={(e) => setEditFormData({ ...editFormData, inactive_device_count: parseInt(e.target.value) || 0 })}
                  data-testid="edit-customer-inactive-device-count"
                />
              </div>
              <div>
                <Label>Arvento Adedi</Label>
                <Input
                  type="number"
                  value={editFormData.arvento_count || 0}
                  onChange={(e) => setEditFormData({ ...editFormData, arvento_count: parseInt(e.target.value) || 0 })}
                  data-testid="edit-customer-arvento"
                />
              </div>
              <div>
                <Label>Kamera Adedi</Label>
                <Input
                  type="number"
                  value={editFormData.camera_count || 0}
                  onChange={(e) => setEditFormData({ ...editFormData, camera_count: parseInt(e.target.value) || 0 })}
                  data-testid="edit-customer-camera"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>İptal</Button>
              <Button onClick={handleUpdateCustomer} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-customer-edit">
                Kaydet
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Analysis Dialog */}
      <Dialog open={showAiAnalysisDialog} onOpenChange={setShowAiAnalysisDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Brain className="w-6 h-6 mr-2 text-purple-600" />
              AI Müşteri Analizi
            </DialogTitle>
          </DialogHeader>
          {aiAnalysis && (
            <div className="space-y-4">
              {/* Statistics */}
              <div className="grid grid-cols-5 gap-3">
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-600 mb-1">Ziyaret</p>
                  <p className="text-2xl font-bold text-blue-600">{aiAnalysis.statistics.total_visits}</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-600 mb-1">Arama</p>
                  <p className="text-2xl font-bold text-purple-600">{aiAnalysis.statistics.total_calls}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-600 mb-1">Satış</p>
                  <p className="text-2xl font-bold text-green-600">{aiAnalysis.statistics.total_sales}</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-600 mb-1">Cihaz</p>
                  <p className="text-2xl font-bold text-orange-600">{aiAnalysis.statistics.total_devices}</p>
                </div>
                <div className="bg-indigo-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-600 mb-1">Ciro</p>
                  <p className="text-lg font-bold text-indigo-600">₺{aiAnalysis.statistics.total_revenue.toLocaleString('tr-TR')}</p>
                </div>
              </div>

              {/* AI Analysis */}
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-3 text-gray-800">AI Analiz Sonucu</h3>
                <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">
                  {aiAnalysis.analysis}
                </div>
              </div>

              {/* Timestamp */}
              <p className="text-xs text-gray-500 text-right">
                Analiz Tarihi: {new Date(aiAnalysis.timestamp).toLocaleString('tr-TR')}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bilgi Tamamlama Dialog */}
      <Dialog open={showCompleteInfoDialog} onOpenChange={setShowCompleteInfoDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Müşteri Bilgilerini Tamamlayın</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Satış yapabilmek için önce müşteri bilgilerini tamamlamanız gerekmektedir.
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Telefon *</Label>
                <Input
                  value={customer.phone || ''}
                  onChange={(e) => setCustomer({ ...customer, phone: e.target.value })}
                  placeholder="5XX XXX XX XX"
                />
              </div>
              
              <div>
                <Label>E-posta</Label>
                <Input
                  value={customer.email || ''}
                  onChange={(e) => setCustomer({ ...customer, email: e.target.value })}
                  type="email"
                />
              </div>
            </div>
            
            <div>
              <Label>Adres *</Label>
              <Input
                value={customer.address || ''}
                onChange={(e) => setCustomer({ ...customer, address: e.target.value })}
                placeholder="Cadde, sokak, bina no..."
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Şehir *</Label>
                <Input
                  value={customer.city || ''}
                  onChange={(e) => setCustomer({ ...customer, city: e.target.value })}
                />
              </div>
              
              <div>
                <Label>İlçe</Label>
                <Input
                  value={customer.district || ''}
                  onChange={(e) => setCustomer({ ...customer, district: e.target.value })}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Vergi No *</Label>
                <Input
                  value={customer.tax_number || ''}
                  onChange={(e) => setCustomer({ ...customer, tax_number: e.target.value })}
                />
              </div>
              
              <div>
                <Label>Vergi Dairesi *</Label>
                <Input
                  value={customer.tax_office || ''}
                  onChange={(e) => setCustomer({ ...customer, tax_office: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label>İletişim Kişisi</Label>
              <Input
                value={customer.contact_person || ''}
                onChange={(e) => setCustomer({ ...customer, contact_person: e.target.value })}
              />
            </div>
            
            <div className="flex gap-2 justify-end pt-4">
              <Button variant="outline" onClick={() => setShowCompleteInfoDialog(false)}>
                İptal
              </Button>
              <Button 
                onClick={async () => {
                  try {
                    // Prospect mı customer mı kontrol et
                    const updateFunc = isProspect ? updateProspect : updateCustomer;
                    await updateFunc(actualId, customer);
                    toast.success('Bilgiler güncellendi');
                    setShowCompleteInfoDialog(false);
                    setShowSaleDialog(true);
                  } catch (error) {
                    console.error('Update error:', error);
                    toast.error('Bilgiler güncellenirken hata oluştu: ' + (error.response?.data?.detail || error.message));
                  }
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Kaydet ve Satış Yap
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default CustomerDetail;